import { ReactNode } from 'react';
import { IconFileWithItemsOutline, IconPlus, IconUpload } from '@iwoca/icons';
import { Button, Card, ErrorMessage, FileDrop, Label } from '@iwoca/orion';
import clsx from 'clsx';
import styles from './FileUpload.module.css';

export type TFileUploadValue<TDoc extends string = string> = {
  file: File;
  docType: TDoc;
};

type TProps<TDoc extends string> = {
  docType: TDoc;
  label: ReactNode;
  instruction: ReactNode;
  selectedFile?: TFileUploadValue<TDoc>;
  errorMessage?: string;
  uploadedLabel?: ReactNode;
  fileDropClassName?: string;
  cardClassName?: string;
  disabled?: boolean;
  onChange: (file?: TFileUploadValue<TDoc>) => void;
};

export const FileUpload = <TDoc extends string>({
  docType,
  label,
  instruction,
  selectedFile,
  errorMessage,
  uploadedLabel = 'Document uploaded',
  fileDropClassName,
  cardClassName,
  disabled = false,
  onChange,
}: TProps<TDoc>) => {
  const handleOnChange = (files: File[]) => {
    // Should only be one file uploaded per docType, so it will replace the previously selected file
    files.forEach((file) => {
      onChange({ file, docType });
    });
  };

  return (
    <div className={styles.fileDropContainer}>
      <Label>{label}</Label>
      <div className={styles.instruction}>{instruction}</div>

      {!selectedFile && (
        <>
          <FileDrop
            multiple={false}
            className={clsx(styles.fileDrop, fileDropClassName)}
            accept=".jpg, .jpeg, .png, .pdf"
            data-testid={`${docType}-file-upload`}
            onChange={handleOnChange}
            stacked={false}
            disabled={disabled}
          />
          {errorMessage && (
            <ErrorMessage className={styles.errorMessage}>
              {errorMessage}
            </ErrorMessage>
          )}
        </>
      )}

      {selectedFile && (
        <Card
          key={`${selectedFile.docType}-${selectedFile.file.name}`}
          className={clsx(styles.card, cardClassName)}
        >
          <div className={styles.fileNameContainer}>
            <IconFileWithItemsOutline
              width={20}
              height={20}
              className={styles.icon}
            />
            <p className={styles.name}>{selectedFile.file.name}</p>
            <Button
              data-testid="remove-button"
              className={styles.removeButton}
              size="small"
              variant="tertiary"
              disabled={disabled}
              onClick={() => onChange(undefined)}
            >
              <IconPlus height={20} width={20} className={styles.removeIcon} />
            </Button>
          </div>
          <div className={styles.uploadedText}>
            <IconUpload width={16} height={16} className={styles.icon} />
            <span>{uploadedLabel}</span>
          </div>
        </Card>
      )}
    </div>
  );
};
